import { TAB, ESCAPE_KEYS } from "../utils/keys";
import { getFocusableElements } from "../utils/focusable";
export default class NavMenu {
  constructor(el) {
    this.menu = el;
    this.lastBtn = null;
    this.targetPanel = null;
    this.toggles = document.querySelectorAll(".js--nav-menu-toggle");
    this.closeBtn = el.querySelector(".js--nav-menu-close");
    this.menuBody = el.querySelector(".nav-menu__body");
    this.navBtns = el.querySelectorAll(".js-nav-btn");
    this.panels = el.querySelectorAll(".nav-menu__panel");
    this.defaultPanel = el.querySelector(".nav-menu__panel--default");

    if (!this.toggles.length) return;

    this.DESKTOP_MQ = window.matchMedia("(min-width: 1070px)");
    // this.FOCUSABLE_SELECTOR =
    //   "a[href], button:not([disabled]), input:not([disabled])";

    this.setup();
    this.bindEvents();
  }

  setup() {
    this.menu.id = "menu-overlay";
    this.toggles.forEach((toggle) => {
      toggle.setAttribute("aria-expanded", "false");
      toggle.setAttribute("aria-controls", "menu-overlay");
    });

    // Mark default panel immediately so CSS transitions have a defined starting state.
    this.defaultPanel?.classList.add("is-panel-visible");

    this.updateAriaControls();
  }

  // ---------------------------------------------------------------------------
  // aria-controls: points to the desktop panel or the mobile accordion
  // depending on the current viewport, since they live in different DOM regions.
  // ---------------------------------------------------------------------------

  updateAriaControls() {
    this.navBtns.forEach((btn) => {
      const id = btn.dataset.panel;
      btn.setAttribute(
        "aria-controls",
        this.DESKTOP_MQ.matches ? `panel-${id}` : `accordion-${id}`,
      );
    });
  }

  // ---------------------------------------------------------------------------
  // Returns all keyboard-reachable elements currently visible inside the menu.
  // ---------------------------------------------------------------------------

  // getFocusable(target) {
  //   return [...target.querySelectorAll(this.FOCUSABLE_SELECTOR)].filter(
  //     (el) => {
  //       if (el.getAttribute("tabindex") === "-1") return false;
  //       if (el.closest("[hidden]")) return false;
  //       // Catches elements inside display:none parents (e.g. utility list on desktop)
  //       if (!el.offsetWidth && !el.offsetHeight) return false;
  //       return true;
  //     },
  //   );
  // }

  // ---------------------------------------------------------------------------
  // Inert: prevent AT from reaching background content while menu is open.
  // ---------------------------------------------------------------------------

  setBackgroundInert(shouldInert) {
    [...document.body.children].forEach((child) => {
      if (child === this.menu) return;
      shouldInert
        ? child.setAttribute("inert", "")
        : child.removeAttribute("inert");
    });
  }

  // ---------------------------------------------------------------------------
  // Open / close
  // ---------------------------------------------------------------------------

  open() {
    this.menu.classList.add("nav-menu--is-open");
    this.toggles.forEach((toggle) =>
      toggle.setAttribute("aria-expanded", "true"),
    );
    this.setBackgroundInert(true);
    // The first rAF allows the rendering pipeline to proceed to style recalculation (where visibility: hidden → visible takes effect).
    // The second rAF fires in the subsequent frame when the computed style is already visible, so focus() succeeds.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => this.closeBtn?.focus()),
    );
  }

  close() {
    this.resetPanels();
    this.setBackgroundInert(false);
    this.menu.classList.remove("nav-menu--is-open");
    this.toggles.forEach((toggle) =>
      toggle.setAttribute("aria-expanded", "false"),
    );
    this.activeToggle?.focus();
    this.lastBtn = null;
    this.targetPanel = null;
  }

  resetPanels() {
    this.navBtns.forEach((btn) => {
      btn.setAttribute("aria-expanded", "false");
      const accordion = btn.nextElementSibling;
      if (accordion?.classList.contains("nav-menu__accordion")) {
        accordion.hidden = true;
      }
    });
    this.panels.forEach((panel) => {
      if (!panel.classList.contains("nav-menu__panel--default")) {
        panel.hidden = true;
        panel.classList.remove("is-panel-visible");
      }
    });
    this.defaultPanel?.removeAttribute("hidden");
    this.defaultPanel?.classList.add("is-panel-visible");
  }

  // ---------------------------------------------------------------------------
  // Panel / accordion activation
  // ---------------------------------------------------------------------------

  activatePanel(btn) {
    const panelId = btn.dataset.panel;
    const targetPanel = this.menu.querySelector(`#panel-${panelId}`);
    const isExpanded = btn.getAttribute("aria-expanded") === "true";

    // Collapse all accordions first (panels are handled below)
    this.navBtns.forEach((b) => {
      b.setAttribute("aria-expanded", "false");
      const accordion = b.nextElementSibling;
      if (accordion?.classList.contains("nav-menu__accordion")) {
        accordion.hidden = true;
      }
    });

    if (isExpanded) {
      // Toggle off the currently active item: hide named panel, restore default
      this.panels.forEach((p) => {
        if (!p.classList.contains("nav-menu__panel--default")) {
          p.hidden = true;
          p.classList.remove("is-panel-visible");
        }
      });
      this.defaultPanel?.removeAttribute("hidden");
      this.defaultPanel?.classList.add("is-panel-visible");
      return;
    }

    btn.setAttribute("aria-expanded", "true");

    // Mobile: reveal inline accordion (focus stays on button so user can Tab in)
    const accordion = btn.nextElementSibling;
    if (accordion?.classList.contains("nav-menu__accordion")) {
      accordion.hidden = false;
    }

    // Desktop: swap panels and move focus into the new panel
    if (this.DESKTOP_MQ.matches && targetPanel) {
      this.lastBtn = btn;
      this.targetPanel = targetPanel;

      this.panels.forEach((p) => {
        p.hidden = true;
        p.classList.remove("is-panel-visible");
      });
      targetPanel.hidden = false;
      targetPanel.classList.add("is-panel-visible");
    }
  }

  // ---------------------------------------------------------------------------
  // Keyboard handling: Escape + focus trap
  // ---------------------------------------------------------------------------

  bindEvents() {
    this.DESKTOP_MQ.addEventListener("change", () => {
      this.updateAriaControls();
      this.resetPanels();
    });

    document.addEventListener("keydown", (e) => {
      if (!this.menu.classList.contains("nav-menu--is-open")) return;

      if (ESCAPE_KEYS.includes(e.key)) {
        this.close();
        return;
      }

      if (e.key === TAB) {
        const trap = this.targetPanel || this.menu;
        const focusable = getFocusableElements(trap);

        if (focusable.length < 2) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (
          !e.shiftKey &&
          this.targetPanel &&
          document.activeElement === this.lastBtn
        ) {
          e.preventDefault();
          first.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          this.lastBtn.focus();
          this.targetPanel = null;
          this.lastBtn = null;
        } else {
          return;
        }

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    });

    this.toggles.forEach((toggle) => {
      toggle.addEventListener("click", () => {
        this.activeToggle = toggle;
        this.menu.classList.contains("nav-menu--is-open")
          ? this.close()
          : this.open();
      });
    });

    this.closeBtn?.addEventListener("click", () => this.close());

    this.navBtns.forEach((btn) => {
      btn.addEventListener("click", () => this.activatePanel(btn));
    });

    this.menu.addEventListener("click", (e) => {
      if (this.DESKTOP_MQ.matches && e.target === this.menuBody) {
        this.resetPanels();
      }
    });
  }
}
